/* The cadence strip: what a named person owes this person, and by when.
 *
 * IT IS A SCHEDULE, NOT A SCORE (SITE-RULES 3b). Nothing here is computed from
 * behaviour; buildTrack in @acrostic/model reads evidence against fixed checkpoints
 * and this draws the answer. A checkpoint is met ONLY on evidence - a logged touch, a
 * stage move, a visit - because a checkpoint that goes green when its date passes is
 * a decoration.
 *
 * MISSED CHECKPOINTS ARE A COUNT, NEVER THE NEXT STEP. You cannot go back and do Day 1
 * on day 47, and a strip that tells you to is telling you to do something impossible.
 */
import type { Track } from '@acrostic/model';

export function TrackStrip({ t }: { t: Track }) {
  /* Past twelve checkpoints the pills stop fitting, so everything that is not a
     milestone degrades to a tick. The member track has twenty-four. */
  const dense = t.steps.length > 12;
  return (
    <div className="track">
      <div className={'dots' + (dense ? ' dense' : '')}>
        {t.steps.map((s) => {
          const state = s.done ? 'done' : s.overdue ? 'late' : s.current ? 'now' : '';
          const pill = !dense || s.milestone;
          const said = s.done ? 'met' : s.overdue ? 'missed' : 'upcoming';
          return (
            <span
              key={s.label + s.at}
              className={(pill ? 'cp ' : 'tick ') + state}
              title={`${s.label}: ${s.what} · ${said}`}
              aria-label={`${s.label}: ${said}`}
            >{pill ? s.label.replace(/^Day /, '') : ''}</span>
          );
        })}
      </div>
      <div className="meter">
        <i style={{ width: t.pct + '%', background: t.overdue ? 'var(--risk-fill)' : 'var(--good-fill)' }} />
      </div>
    </div>
  );
}

/** The whole panel: where they are in the cadence, and the one thing owed next. */
export function TrackCard({ t, eyebrow, complete, children }: {
  t: Track;
  eyebrow: string;
  /** What to say when every checkpoint is behind them. */
  complete: string;
  children?: React.ReactNode;
}) {
  return (
    <section className="panel track-side">
      <div className="eyebrow t-label-xsmall">
        {eyebrow} · day {t.day}{t.overdue ? ` · ${t.overdue} missed` : ''}
      </div>
      <h3 className="t-heading-h3" style={{ marginTop: 4 }}>
        {t.nextStep ? 'Next: ' + t.nextStep.label : t.overdue ? 'Past the last checkpoint' : 'Track complete'}
      </h3>
      <p className="t-body-small muted track-what">
        {t.nextStep ? t.nextStep.what : complete}
      </p>
      <TrackStrip t={t} />
      {children}
    </section>
  );
}
